<div class="mail-subject" [class.disabled]="isMailSubjectDisabled()">
  @if (mailboxLoading) {
    <i class="pi pi-spinner"></i>
  }
  <div class="mail-subject__option">
    <p-radiobutton
      [(ngModel)]="isMailSubjectSelected"
      [disabled]="isMailSubjectDisabled()"
      [value]="false"
      (ngModelChange)="onMailSubjectChange(null)"
      name="mailSubject"
      inputId="newMessage"
    >
    </p-radiobutton>
    <label for="newMessage">Send as a new message</label>
  </div>
  <div class="mail-subject__option">
    <p-radiobutton
      [(ngModel)]="isMailSubjectSelected"
      [disabled]="isMailSubjectDisabled()"
      [value]="true"
      (ngModelChange)="onMailSubjectChange(mailSubjects[0].value)"
      name="mailSubject"
      inputId="replyMessage"
    >
    </p-radiobutton>
    <label for="replyMessage">Send in reply to:</label>
    <p-select
      showClear="true"
      emptyMessage="Not found"
      [disabled]="isMailSubjectDisabled() || !isMailSubjectSelected"
      [options]="mailSubjects"
      [placeholder]="
        isMailSubjectSelected ? this.getSubjectValue() : 'Please select'
      "
      (onChange)="onMailSubjectChange($event.value)"
    >
    </p-select>
  </div>
</div>

@if (quote().getStatus() === quoteStatus.draft) {
  <form [formGroup]="mailForm" class="mail-editor">
    <div class="mail-editor__header">
      <span class="mail-editor__title">Email details</span>
      @if (previewLoading) {
        <i class="pi pi-spinner"></i>
      }
    </div>

    @for (recipient of recipientFields; track recipient.name) {
      <div class="mail-editor__row">
        <span class="mail-editor__label">{{ recipient.label }}</span>
        <div class="mail-editor__value">
          <div class="mail-editor__field">
            <p-autocomplete
              [formControlName]="recipient.name"
              [suggestions]="results"
              (onKeyUp)="onKeyUpField($event, recipient.name)"
              (onBlur)="onBlurField($event, recipient.name)"
              (completeMethod)="search($event)"
              [showEmptyMessage]="true"
              [multiple]="true"
            >
              <ng-template #item let-value>
                <span>{{ nameRecipient(value) }}</span>
              </ng-template>
              <ng-template #selecteditem let-value>
                <span data-cy="recipient-value">{{
                  resolveRecipient(value)
                }}</span>
              </ng-template>
            </p-autocomplete>
          </div>
          @if (
            mailForm.controls[recipient.name].touched &&
            mailForm.controls[recipient.name].invalid
          ) {
            <p-message
              severity="error"
              variant="simple"
              size="small"
              class="mail-editor__error"
              data-cy="recipient-error"
            >
              Please select a valid email address, for example
              name&#64;domain.com
            </p-message>
          }
        </div>
      </div>
    }

    <div class="mail-editor__row">
      <span class="mail-editor__label">Subject:</span>
      <div class="mail-editor__value">
        <div class="mail-editor__field">
          <app-wysiwyg
            #subjectWysiwyg
            [showPlaceholders]="true"
            [placeholderValues]="placeholderValues"
            [showToolbarItems]="false"
            [value]="mailForm.controls.subject.value"
            (valueChanged)="updateSubject($event)"
            class="mail-editor__subject"
            [class.has-error]="!mailForm.controls.subject.valid"
          >
          </app-wysiwyg>
          @if (isRestoreDefaultSubjectVisible()) {
            <button
              pButton
              [text]="true"
              type="button"
              class="mail-editor__restore"
              data-cy="restore-subject"
              (click)="restoreDefaultSubject()"
            >
              Restore default
            </button>
          }
        </div>
      </div>
    </div>

    <div class="mail-editor__row mail-editor__row--message">
      <span class="mail-editor__label">Message:</span>
      <div class="mail-editor__value">
        <app-wysiwyg
          #messageWysiwyg
          [showPlaceholders]="true"
          [placeholderValues]="placeholderValues"
          [showToolbarItems]="true"
          [minHeight]="true"
          [value]="mailForm.controls.message.value"
          (valueChanged)="updateMessage($event)"
          [class.has-error]="!mailForm.controls.message.valid"
        >
        </app-wysiwyg>
        @if (isRestoreDefaultMessageVisible()) {
          <button
            pButton
            [text]="true"
            type="button"
            class="mail-editor__restore"
            data-cy="restore-message"
            (click)="restoreDefaultMessage()"
          >
            Restore default
          </button>
        }
      </div>
    </div>

    <div class="mail-editor__row mail-editor__row--attachments">
      <span class="mail-editor__label">Attachment:</span>
      <div class="mail-editor__value">
        <app-upload
          label="Add files"
          multiple="true"
          [outlined]="true"
          (fileUploaded)="addAttachment($event)"
        >
        </app-upload>
        <div class="mail-editor__attachments" data-cy="attachment-list">
          @for (
            attachment of mailForm.controls.attachments.value;
            track attachment
          ) {
            <p-chip
              [removable]="true"
              removeIcon="pi pi-times"
              (onRemove)="removeAttachment(attachment)"
            >
              <a
                class="mail-editor__attachment-link"
                target="_blank"
                [href]="attachment"
              >
                <i class="pi pi-paperclip" aria-hidden="true"></i
                >{{ getFileName(attachment) }}
              </a>
            </p-chip>
          }
        </div>
      </div>
    </div>
  </form>
} @else {
  <div class="mail-editor">
    <div class="mail-editor__header">
      <span class="mail-editor__title">Email details</span>
      @if (previewLoading) {
        <i class="pi pi-spinner"></i>
      }
    </div>

    @if (emailPreview) {
      <div class="mail-editor__row">
        <span class="mail-editor__label">To:</span>
        <div class="mail-editor__value">{{ emailPreview.getTo() }}</div>
      </div>

      <div class="mail-editor__row">
        <span class="mail-editor__label">CC:</span>
        <div class="mail-editor__value">{{ emailPreview.getCc() }}</div>
      </div>

      <div class="mail-editor__row">
        <span class="mail-editor__label">BCC:</span>
        <div class="mail-editor__value">{{ emailPreview.getBcc() }}</div>
      </div>

      <div class="mail-editor__row">
        <span class="mail-editor__label">Subject:</span>
        <div class="mail-editor__value">{{ emailPreview.getSubject() }}</div>
      </div>

      <div class="mail-editor__row mail-editor__row--message">
        <span class="mail-editor__label">Message:</span>
        <div class="mail-editor__value">
          <div
            class="mail-editor__preview-message"
            data-cy="preview-value"
            [innerHTML]="emailPreview.getMessage() | safeHtml"
          ></div>
        </div>
      </div>

      @if (emailPreview.getAttachments()?.length) {
        <div class="mail-editor__row mail-editor__row--attachments">
          <span class="mail-editor__label">Attachments:</span>
          <div class="mail-editor__value">
            <div class="mail-editor__attachments">
              @for (
                attachment of emailPreview.getAttachments();
                track attachment
              ) {
                <p-chip>
                  <a
                    class="mail-editor__attachment-link"
                    target="_blank"
                    [href]="attachment"
                  >
                    <i class="pi pi-paperclip" aria-hidden="true"></i
                    >{{ getFileName(attachment) }}
                  </a>
                </p-chip>
              }
            </div>
          </div>
        </div>
      }
    }
  </div>
}
